// src/components/Profile/ProfileEditForm.jsx
import React, { useState, useEffect } from 'react';
import {
    Paper,
    Grid,
    Box,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateProfile } from '../../hooks/profile/useUpdateProfile';
import FormInput from '../inputs/FormInput';
import Btn from '../global/Btn';
import { useTheme } from '@mui/material/styles';

const ProfileEditForm = ({ currentData, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { updateProfile, isUpdating } = useUpdateProfile();

    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        gender: '',
        birthdate: '',
    });

    useEffect(() => {
        if (currentData) {
            setForm({
                first_name: currentData.first_name || '',
                last_name: currentData.last_name || '',
                gender: currentData.gender || '',
                birthdate: currentData.birthdate || '',
            });
        }
    }, [currentData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {};

        // فقط إرسال الحقول التي تغيرت أو لها قيمة
        if (form.first_name && form.first_name !== currentData?.first_name) {
            payload.first_name = form.first_name;
        }
        if (form.last_name && form.last_name !== currentData?.last_name) {
            payload.last_name = form.last_name;
        }
        // Gender - نرسله إذا تغير (حتى لو كان فارغاً)
        if (form.gender !== currentData?.gender) {
            payload.gender = form.gender;
        }
        if (form.birthdate && form.birthdate !== currentData?.birthdate) {
            payload.birthdate = form.birthdate;
        }

        if (Object.keys(payload).length === 0) {
            onCancel();
            return;
        }

        updateProfile(payload, {
            onSuccess: () => {
                onSuccess();
            },
        });
    };

    return (
        <Paper
            sx={{
                p: 3,
                bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
            }}
        >
            <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('profile.editPersonalInfo')}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={t('profile.firstName')}
                            placeholder={t('profile.firstNamePlaceholder')}
                            type="text"
                            name="first_name"
                            value={form.first_name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={t('profile.lastName')}
                            placeholder={t('profile.lastNamePlaceholder')}
                            type="text"
                            name="last_name"
                            value={form.last_name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={t('profile.gender')}
                            type="select"
                            name="gender"
                            value={form.gender}
                            onChange={handleChange}
                            options={[
                                { id: 'male', name: t('profile.male') },
                                { id: 'female', name: t('profile.female') },
                            ]}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={t('profile.birthdate')}
                            type="date"
                            name="birthdate"
                            value={form.birthdate}
                            onChange={handleChange}
                            disableFuture={true}
                        />
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                    <Btn
                        variant="outlined"
                        onClick={onCancel}
                        disabled={isUpdating}
                        color={theme.palette.text.primary}
                        bg="secondary"
                    >
                        {t('common.cancel')}
                    </Btn>
                    <Btn
                        type="submit"
                        loading={isUpdating}
                    >
                        {t('profile.saveChanges')}
                    </Btn>
                </Box>
            </form>
        </Paper>
    );
};

export default ProfileEditForm;
